import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Alert, Button, Col, Form, Row } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import { createForm } from '../../../models/Form';
import { createFormField } from '../../../models/FormField';
import { modelOf } from '../../../prop-types';
import ApiWrapper from '../../../services/ApiWrapper';
import AccountStore from '../../../store/AccountStore';
import ConfigStore from '../../../store/ConfigStore';
import RequestState from '../../../types/RequestState';
import ValidationStatus, {
  errorMap,
} from '../../../types/form/ValidationStatus';
import { isEmail, minLength, notBlank } from '../../../util/formValidators';
import FormGroupField from '../../form/FormGroupField';
import ContentForState from '../../loader/ContentForState';

@observer
class MyAccountInfoForm extends Component {
  constructor(props) {
    super(props);
    const { configStore } = this.props;

    this.state = {
      error: null,
      status: ValidationStatus.NONE,
      requestState: RequestState.NONE,
      requestId: 0,
    };

    const fields = {
      firstname: createFormField({}, [
        minLength(
          <FormattedMessage {...globalTranslations.firstNameTitle} />,
          configStore.formValidation.firstName.minLength
        ),
      ]),
      lastname: createFormField({}, [
        minLength(
          <FormattedMessage {...globalTranslations.lastNameTitle} />,
          configStore.formValidation.lastName.minLength
        ),
      ]),
      email: createFormField({}, [
        notBlank(<FormattedMessage {...globalTranslations.emailTitle} />),
        isEmail(<FormattedMessage {...globalTranslations.emailTitle} />),
      ]),
      telephone: createFormField({}, [
        minLength(
          <FormattedMessage {...globalTranslations.telephoneTitle} />,
          configStore.formValidation.telephone.minLength
        ),
      ]),
    };

    this.form = createForm(fields);
  }

  componentDidMount() {
    this.maybeLoadAccountInfo();
  }

  maybeLoadAccountInfo = () => {
    const { accountStore } = this.props;
    accountStore
      .loadAccount()
      .then(() => {
        this.fieldInitialFill();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  fieldInitialFill = () => {
    const { accountStore } = this.props;
    this.form.fields
      .get('firstname')
      .setValue(accountStore.account.personal_info.firstname);
    this.form.fields
      .get('lastname')
      .setValue(accountStore.account.personal_info.lastname);
    this.form.fields
      .get('email')
      .setValue(accountStore.account.personal_info.email);
    this.form.fields
      .get('telephone')
      .setValue(accountStore.account.personal_info.telephone);
    this.form.validate();
  };

  handleSubmit = (e) => {
    const { apiWrapper } = this.props;

    e.preventDefault();
    this.form.validate();

    if (this.form.valid) {
      this.setState({
        requestState: RequestState.LOADING,
      });

      apiWrapper
        .apiAxios()
        .put('my-account/info', this.form.getFieldValues())
        .then(() => {
          this.setState({
            status: ValidationStatus.SUCCESS,
            requestState: RequestState.LOADED,
            requestId: this.state.requestId + 1,
            error: null,
          });
        })
        .catch((error) => {
          const status = error.response ? error.response.status : 500;

          this.setState({
            requestState: RequestState.ERROR,
            status: errorMap[status],
            requestId: this.state.requestId + 1,
            error,
          });
        });
    }
  };

  getField = (name, message, type) => {
    const field = this.form.fields.get(name);

    return (
      <div
        className={`MyAccountInfoForm__field MyAccountInfoForm__field--${name}`}
      >
        <FormGroupField
          field={field}
          label={<FormattedMessage {...message} />}
          formName="MyAccountInfoForm"
          fieldName={name}
          type={type}
        />
      </div>
    );
  };

  getForm = () => {
    return (
      <Form noValidate onSubmit={this.handleSubmit}>
        <Row>
          <Col md="6">
            <div className="MyAccountInfoForm__row">
              {this.getField('firstname', globalTranslations.firstNameTitle)}
              {this.getField('lastname', globalTranslations.lastNameTitle)}
              {this.getField('email', globalTranslations.emailTitle, 'email')}
              {this.getField(
                'telephone',
                globalTranslations.telephoneTitle,
                'tel'
              )}
              <Button
                className="MyAccountInfoForm__submit"
                color="primary"
                disabled={
                  this.form.valid === false ||
                  this.state.requestState === RequestState.LOADING
                }
              >
                {<FormattedMessage {...globalTranslations.update} />}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    );
  };

  getErrorMessages = () => {
    const errors = Object.values(this.state.error.response.data.errors);
    return errors.length > 0 && errors;
  };

  renderStatus = () => {
    const { status, error, requestId } = this.state;
    const errors = error && this.getErrorMessages();
    let message = null;

    switch (status) {
      case ValidationStatus.SUCCESS:
        message = (
          <Alert color="success" key={requestId}>
            <FormattedMessage
              id="account.userInfo.SUCCESS"
              defaultMessage="Your customer account information was updated successfully."
            />
          </Alert>
        );
        break;
      case ValidationStatus.VALIDATION_ERROR:
        message = errors && (
          <Alert color="danger" key={requestId}>
            {errors.flat().map((errorMessages) => (
              <div key={errorMessages}>{errorMessages}</div>
            ))}
          </Alert>
        );
        break;
      case ValidationStatus.UNEXPECTED_ERROR:
        message =
          (error && <ErrorHandler error={createErrorModel(error)} />) || null;
        break;
      case ValidationStatus.NONE:
      case RequestState.LOADING:
      default:
        message = null;
        break;
    }

    return message;
  };

  render() {
    const { accountStore } = this.props;
    return (
      <ContentForState
        state={accountStore.state}
        error={accountStore.lastError}
        forLoaded={() => {
          return (
            <div className="MyAccountInfoForm">
              {this.renderStatus()}
              {this.state.status !== null && this.getForm()}
            </div>
          );
        }}
      />
    );
  }
}

MyAccountInfoForm.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  apiWrapper: PropTypes.instanceOf(ApiWrapper).isRequired,
};

export default inject(
  'apiWrapper',
  'configStore',
  'accountStore'
)(MyAccountInfoForm);
